// Third Party
import * as React from 'react';
import Image from 'next/image';
import { GalleryAsset } from '@gql';

// Utils
import { GRAY_PIXEL_DATA_URL } from '@utils/constants';
import { imagekitLoader } from '@utils/imagekit-loader';
import { notEmpty } from '@utils/not-empty';

// UI
import { useToggle } from '@hooks';
import { ImageGalleryModal } from './image-gallery-modal';
import { concatenateAndReplace } from '@utils/test-helpers';
import { Button } from '@components/button';
import { Carousel } from '@components/carousel';

type Props = {
  galleryAssets: Pick<GalleryAsset, 'description' | 'url'>[];
  altTag: string;
  shouldShowAllImages: boolean;
  shouldWatermarkImage: boolean;
};

export const ImageMural: React.FC<Props> = ({
  galleryAssets,
  altTag,
  shouldShowAllImages,
  shouldWatermarkImage,
}) => {
  const galleryImages = galleryAssets
    .map(({ url }) => {
      if (url) {
        return { url };
      } else {
        return null;
      }
    })
    .filter(notEmpty);

  const { on, setOff, setOn } = useToggle(false);
  const onlyShowHero = galleryAssets.length < 5;
  const [galleryIndex, setGalleryIndex] = React.useState(0);

  const selectedGalleryAssetIndex = (index: number): void => {
    setGalleryIndex(index);
    setOn();
  };

  // If modal is active add class to body to prevent scroll
  React.useEffect(() => {
    if (on) {
      document.body.classList.add('modalActive');
    } else {
      document.body.classList.remove('modalActive');
    }
  }, [on]);

  React.useEffect(() => {
    const handleKeyUp = (event: KeyboardEvent) => {
      switch (event.code) {
        case 'Escape':
          setOff();
          break;
        default:
          break;
      }
    };

    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, []);

  type SliderImage = {
    url: string;
  };

  let images: SliderImage[] = [];

  if (!Array.isArray(galleryAssets) || galleryAssets.length > 0) {
    galleryAssets.forEach((galleryAsset) => {
      if (galleryAsset.url) {
        images.push({ url: galleryAsset.url });
      }
    });
  }

  return (
    <div className="relative w-full">
      {galleryAssets.length > 1 && shouldShowAllImages && (
        <div className="relative -mx-4 aspect-[3/2] h-full overflow-hidden sm:hidden">
          <div className="aspect-[3/2]">
            <Carousel
              showDots={images.length < 10 && shouldShowAllImages}
              showArrows={shouldShowAllImages}
              className="relative block h-full w-full"
              onSlideChange={(index) => setGalleryIndex(index)}
            >
              {images.map((image, index) => (
                <button
                  onClick={() => selectedGalleryAssetIndex(index)}
                  key={index}
                  className="relative block aspect-[3/2] h-auto w-full"
                >
                  <Image
                    layout="fill"
                    src={image.url}
                    alt={altTag}
                    placeholder="blur"
                    className="object-center object-cover"
                    loader={imagekitLoader}
                    blurDataURL={GRAY_PIXEL_DATA_URL}
                    lazyBoundary="400px"
                    sizes="100vw"
                    priority={index == 0}
                    data-testid={concatenateAndReplace(
                      `${altTag}-image-${index}`
                    )}
                  />
                </button>
              ))}
            </Carousel>
          </div>
        </div>
      )}
      {galleryAssets.length > 1 && shouldShowAllImages && (
        <div>
          <Button
            aria-label="expand"
            data-testid="@listing/view-gallery-photos-button"
            onClick={(): void => setOn()}
            variant="outlined"
            color="primary"
            size="sm"
            className="absolute bottom-4 right-0 block bg-white focus:outline-none sm:hidden"
          >
            {`${galleryIndex + 1} / ${galleryImages.length}`}
          </Button>
          <Button
            aria-label="expand"
            data-testid="@listing/view-photos-button"
            onClick={(): void => setOn()}
            variant="outlined"
            color="primary"
            size="sm"
            className="absolute bottom-4 right-4 z-10 hidden bg-white focus:outline-none sm:block"
          >
            {`View ${galleryImages.length} Photos`}
          </Button>
        </div>
      )}

      {galleryImages.length > 0 && on && (
        <ImageGalleryModal
          originalIndex={galleryIndex}
          modalIsOpen={on}
          closeModal={() => setOff()}
          galleryImages={galleryImages}
          altTag={altTag}
        />
      )}

      {galleryAssets.length === 1 && (
        <div
          className="sm:aspect-auto relative block aspect-[3/2] w-full hover:opacity-80 sm:hidden sm:aspect-3"
          onClick={() => selectedGalleryAssetIndex(0)}
        >
          <Image
            alt={altTag}
            className="object-cover object-center"
            layout="fill"
            placeholder="blur"
            blurDataURL={GRAY_PIXEL_DATA_URL}
            src={galleryAssets.length > 0 ? galleryAssets[0].url! : ''}
            loader={imagekitLoader}
            sizes="100vw"
            priority={true}
            data-testid={concatenateAndReplace(`${altTag}-image`)}
          />
          {shouldWatermarkImage && (
            <div>
              <div className="absolute bottom-0 right-0 h-20 w-32 bg-gray-dark opacity-50 sm:rounded-md" />
              <div className="absolute bottom-0 right-0 flex h-20 w-32 flex-col justify-center">
                <img
                  src="/assets/svg/iconPin.svg"
                  alt="Savvy pin"
                  className="mx-auto h-6 w-6"
                />
              </div>
            </div>
          )}
        </div>
      )}

      {onlyShowHero || !shouldShowAllImages ? (
        <button
          className="sm:aspect-auto relative hidden aspect-[3/2] w-full hover:opacity-80 sm:block sm:aspect-3"
          onClick={() => selectedGalleryAssetIndex(0)}
        >
          <Image
            alt={altTag}
            className="object-cover object-center"
            layout="fill"
            placeholder="blur"
            blurDataURL={GRAY_PIXEL_DATA_URL}
            src={galleryAssets.length > 0 ? galleryAssets[0].url! : ''}
            loader={imagekitLoader}
            sizes="100vw"
            priority={true}
            data-testid={concatenateAndReplace(`${altTag}-image`)}
          />
          {shouldWatermarkImage && (
            <div>
              <div className="absolute bottom-0 right-0 h-20 w-32 bg-gray-dark opacity-50 sm:rounded-md" />
              <div className="absolute bottom-0 right-0 flex h-20 w-32 flex-col justify-center">
                <img
                  src="/assets/svg/iconPin.svg"
                  alt="Savvy pin"
                  className="mx-auto h-6 w-6"
                />
              </div>
            </div>
          )}
        </button>
      ) : (
        <div className="xl:aspect-auto hidden aspect-[3/2] w-full grid-rows-2 sm:grid sm:aspect-3 sm:grid-cols-4 sm:gap-2 xl:h-[30rem]">
          <button
            className="relative col-span-2 row-span-2 h-full w-full hover:opacity-80"
            onClick={() => selectedGalleryAssetIndex(0)}
          >
            <Image
              alt={altTag}
              className="rounded-l-lg object-cover object-center"
              layout="fill"
              placeholder="blur"
              blurDataURL={GRAY_PIXEL_DATA_URL}
              src={galleryAssets[0].url!}
              loader={imagekitLoader}
              sizes="(min-width: 768px) 50vw, 100vw"
              priority={true}
              data-testid={concatenateAndReplace(`${altTag}-image`)}
            />
          </button>

          <button
            className="relative col-span-1 hidden h-full w-full rounded-md hover:opacity-80 sm:block"
            onClick={() => selectedGalleryAssetIndex(1)}
          >
            <Image
              alt={altTag}
              className="object-cover object-center"
              layout="fill"
              placeholder="blur"
              blurDataURL={GRAY_PIXEL_DATA_URL}
              src={galleryAssets[1].url!}
              loader={imagekitLoader}
              sizes="25vw"
              data-testid={concatenateAndReplace(`${altTag}-image`)}
            />
          </button>

          <button
            className="relative col-span-1 hidden h-full w-full hover:opacity-80 sm:block"
            onClick={() => selectedGalleryAssetIndex(2)}
          >
            <Image
              alt={altTag}
              className="rounded-tr-lg object-cover object-center"
              layout="fill"
              placeholder="blur"
              blurDataURL={GRAY_PIXEL_DATA_URL}
              src={galleryAssets[2].url!}
              loader={imagekitLoader}
              sizes="25vw"
              data-testid={concatenateAndReplace(`${altTag}-image`)}
            />
          </button>
          <button
            className="relative col-span-1 hidden h-full w-full hover:opacity-80 sm:block"
            onClick={() => selectedGalleryAssetIndex(3)}
          >
            <Image
              alt={altTag}
              className="object-cover object-center"
              layout="fill"
              placeholder="blur"
              blurDataURL={GRAY_PIXEL_DATA_URL}
              src={galleryAssets[3].url!}
              loader={imagekitLoader}
              sizes="25vw"
              data-testid={concatenateAndReplace(`${altTag}-image`)}
            />
          </button>
          <button
            className="relative col-span-1 hidden h-full w-full hover:opacity-80 sm:block"
            onClick={() => selectedGalleryAssetIndex(4)}
          >
            <Image
              alt={altTag}
              className="rounded-br-lg object-cover object-center"
              layout="fill"
              placeholder="blur"
              blurDataURL={GRAY_PIXEL_DATA_URL}
              src={galleryAssets[4].url!}
              loader={imagekitLoader}
              sizes="25vw"
              data-testid={concatenateAndReplace(`${altTag}-image`)}
            />
          </button>
        </div>
      )}
    </div>
  );
};
